import type { ClinicDayHours } from './clinic-hours'

export const DEFAULT_DISPLAY_START_MINUTE = 9 * 60
export const DEFAULT_DISPLAY_END_MINUTE = 18 * 60
/** Layout length for a visit without a usable end; it is never shown as a duration. */
export const UNKNOWN_DURATION_MINUTES = 30

export type TimedEntry<T> = {
  item: T
  startMinute: number
  /** Null when the record has no valid end. */
  endMinute: number | null
}

export type PlacedEntry<T> = TimedEntry<T> & {
  /** Zero-based column inside the entry's group of overlapping visits. */
  column: number
  /** Columns that group needs, so every visit in it gets the same width. */
  columns: number
}

function layoutEnd(entry: TimedEntry<unknown>) {
  return entry.endMinute !== null && entry.endMinute > entry.startMinute
    ? entry.endMinute
    : entry.startMinute + UNKNOWN_DURATION_MINUTES
}

/**
 * Side-by-side columns for visits that overlap in time (not only those that
 * start together): each group of transitively overlapping visits is split
 * into as few columns as possible, earliest first.
 */
export function placeLane<T>(
  entries: Array<TimedEntry<T>>
): Array<PlacedEntry<T>> {
  const sorted = [...entries].sort(
    (left, right) =>
      left.startMinute - right.startMinute || layoutEnd(right) - layoutEnd(left)
  )
  const placed: Array<PlacedEntry<T>> = []
  let group: Array<PlacedEntry<T>> = []
  let columnEnds: number[] = []
  let groupEnd = -Infinity
  const closeGroup = () => {
    for (const entry of group) entry.columns = columnEnds.length
    placed.push(...group)
    group = []
    columnEnds = []
  }
  for (const entry of sorted) {
    if (entry.startMinute >= groupEnd) closeGroup()
    let column = columnEnds.findIndex((end) => end <= entry.startMinute)
    if (column === -1) column = columnEnds.length
    columnEnds[column] = layoutEnd(entry)
    groupEnd = Math.max(groupEnd === -Infinity ? 0 : groupEnd, layoutEnd(entry))
    group.push({ ...entry, column, columns: 1 })
  }
  closeGroup()
  return placed
}

/** The widest group in a lane: how many visits run side by side at most. */
export function laneColumns(placed: Array<PlacedEntry<unknown>>) {
  return placed.reduce((most, entry) => Math.max(most, entry.columns), 1)
}

/**
 * The hours the Day grid shows: 9 AM to 6 PM, widened to whole hours when a
 * visit starts earlier or ends later, so every visit sits in its own lane.
 */
export function displayRange(entries: Array<TimedEntry<unknown>>) {
  let start = DEFAULT_DISPLAY_START_MINUTE
  let end = DEFAULT_DISPLAY_END_MINUTE
  for (const entry of entries) {
    start = Math.min(start, Math.floor(entry.startMinute / 60) * 60)
    end = Math.max(end, Math.ceil(layoutEnd(entry) / 60) * 60)
  }
  return { startMinute: Math.max(0, start), endMinute: Math.min(24 * 60, end) }
}

/** Parts of the shown range outside the hours Flio can schedule. */
export function unavailableIntervals(
  dayHours: ClinicDayHours,
  range: { startMinute: number; endMinute: number }
) {
  if (dayHours.status === 'fallback') return []
  if (dayHours.status === 'closed') {
    return [{ startMinute: range.startMinute, endMinute: range.endMinute }]
  }
  const available = dayHours.intervals
    .map((interval) => ({
      startMinute: Math.max(range.startMinute, interval.startMinute),
      endMinute: Math.min(range.endMinute, interval.endMinute),
    }))
    .filter((interval) => interval.endMinute > interval.startMinute)
    .sort((left, right) => left.startMinute - right.startMinute)
  const gaps: Array<{ startMinute: number; endMinute: number }> = []
  let cursor = range.startMinute
  for (const interval of available) {
    if (interval.startMinute > cursor) {
      gaps.push({ startMinute: cursor, endMinute: interval.startMinute })
    }
    cursor = Math.max(cursor, interval.endMinute)
  }
  if (cursor < range.endMinute) {
    gaps.push({ startMinute: cursor, endMinute: range.endMinute })
  }
  return gaps
}

export function clockLabel(minutes: number, { compact = false } = {}) {
  const hour = Math.floor(minutes / 60) % 24
  const minute = minutes % 60
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  const time = minute
    ? `${displayHour}:${String(minute).padStart(2, '0')}`
    : `${displayHour}`
  return compact ? time : `${time} ${suffix}`
}

/** "10 AM–12:30 PM, 1:30–5 PM": the meridiem is said once per run. */
export function hoursLabel(
  intervals: Array<{ startMinute: number; endMinute: number }>
) {
  return intervals
    .map(({ startMinute, endMinute }) => {
      const sameHalf = startMinute < 720 === endMinute < 720
      return `${clockLabel(startMinute, { compact: sameHalf })}–${clockLabel(endMinute)}`
    })
    .join(', ')
}
